import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  CreditCard,
  Building,
  Smartphone,
  Plus,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const paymentMethods = ['Cash', 'UPI', 'Bank Transfer', 'Razorpay', 'Cheque'];

const ClientPayment = () => {
  const { clientId } = useParams();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch client data
  const {
    data: client,
    isLoading: clientLoading,
    error: clientError,
  } = useQuery({
    queryKey: ['client', clientId],
    queryFn: async () => {
      return pb.collection('leads').getOne(clientId);
    },
    enabled: !!clientId,
  });

  // Fetch all payments for this client
  const {
    data: payments = [],
    isLoading: paymentsLoading,
  } = useQuery({
    queryKey: ['payments', clientId],
    queryFn: async () => {
      return pb.collection('payments').getFullList({
        filter: `leadId = "${clientId}"`,
        sort: '-paymentDate',
      });
    },
    enabled: !!clientId,
  });

  // Create payment mutation
  const createPayment = useMutation({
    mutationFn: async (newPayment) => {
      return pb.collection('payments').create(newPayment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payments', clientId]);
      toast.success('Payment recorded successfully');
      setAmount('');
    },
    onError: (error) => {
      console.error('Payment error:', error);
      toast.error('Failed to record payment');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    createPayment.mutate({
      leadId: clientId,
      amount: Number(amount),
      paymentMethod: method,
      paymentDate: paymentDate,
      recordedBy: currentUser?.id,
      status: 'Completed',
    });
  };

  const totalPaid = payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
  const dealPrice = client?.budget ? parseInt(client.budget) || 0 : 0;
  const remaining = Math.max(0, dealPrice - totalPaid);

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (clientError) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500">Failed to load client information.</p>
        <Button asChild className="mt-4">
          <Link to="/office/clients">Back to Clients</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/office/clients/${clientId}`}
          className="flex items-center gap-1 text-slate-600 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Client
        </Link>
        <h1 className="text-2xl font-bold text-slate-800">
          Payments – {client?.name}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Payment Form & History */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Record New Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="amount">Amount (₹)</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="date">Payment Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={createPayment.isPending} className="w-full">
                  {createPayment.isPending ? 'Recording...' : 'Record Payment'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              {paymentsLoading ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : payments?.length === 0 ? (
                <p className="text-sm text-slate-500">No payments recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div>
                        <p className="font-medium">₹{payment.amount.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-slate-500">
                          {payment.paymentMethod} • {new Date(payment.paymentDate).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                        Recorded
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Payment Summary */}
        <div className="space-y-6">
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Payment Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Budget / Deal Price</span>
                <span className="font-semibold">₹{dealPrice.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Total Paid</span>
                <span className="font-semibold text-green-600">₹{totalPaid.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between border-t pt-3">
                <span className="text-sm font-medium">Remaining</span>
                <span className={`font-semibold ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{remaining.toLocaleString('en-IN')}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientPayment;